import { logAxiosError } from 'src/utils/logger/logs';
import { axiosInstance } from './client';
import { BusinessAccountResult } from './types/types';

import { BusinessAccount, BusinessLocation } from './types/business-types';

interface LocationsResponse {
  locations?: BusinessLocation[];
}

interface BusinessResponse {
  accounts: BusinessAccount[];
}

const fetchBusinessAccount = async (
  token: string,
): Promise<BusinessAccountResult> => {
  try {
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const businessResponse = await axiosInstance.get<BusinessResponse>(
      `https://mybusinessbusinessinformation.googleapis.com/v1/accounts/`,
      { headers },
    );

    const businessData = businessResponse.data;
    const accounts = businessData?.accounts ?? [];

    if (accounts.length > 0) {
      // Prefer LOCATION_GROUP accounts (business accounts that usually own locations),
      // then ORGANIZATION, then any remaining types (e.g. PERSONAL) as a last resort.
      const orderedAccounts: BusinessAccount[] = [
        ...accounts.filter(a => a.type === 'LOCATION_GROUP'),
        ...accounts.filter(a => a.type === 'ORGANIZATION'),
      ];

      if (orderedAccounts.length === 0) {
        return { account: undefined, locations: [] };
      }

      for (const account of orderedAccounts) {
        try {
          const locationsResponse = await axiosInstance.get<LocationsResponse>(
            `https://mybusinessbusinessinformation.googleapis.com/v1/${account.name}/locations?readMask=name,title,languageCode,storeCode,phoneNumbers,storefrontAddress,websiteUri,serviceArea,latlng,metadata,profile&pageSize=100`,
            { headers },
          );
          const locations =
            locationsResponse.data.locations?.map(location => {
              const key = location.name.replace('locations/', '');
              return { ...location, key };
            }) ?? [];

          // Return the first account that actually has locations.
          if (locations.length > 0) {
            return {
              account,
              locations,
            };
          }
        } catch (err) {
          // Log per-account errors and try the next account.
          logAxiosError(err);
        }
      }

      // No account had locations; return the first account (best-effort) with an empty list.
      return {
        account: orderedAccounts[0],
        locations: [],
      };
    }
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const status = (error as any)?.response?.status;
    if (status === 401 || status === 403) {
      return { account: undefined, locations: [], unauthenticated: true };
    }
    logAxiosError(error);
  }

  return { account: undefined, locations: [] };
};

export { fetchBusinessAccount };
